import { computePointWindFields, getOptimalWindsArray } from './windUtils';
import {
  isFeedFeature,
  isStandFeature,
  markerTypeSelectValue
} from './pointIdentityPayload';

export const STAND_POINT_FILTER = [
  'all',
  ['==', ['geometry-type'], 'Point'],
  ['==', ['get', 'point_role'], 'stand']
];

export const MARKER_POINT_FILTER = [
  'all',
  ['==', ['geometry-type'], 'Point'],
  ['==', ['get', 'point_role'], 'marker']
];

const MARKER_ICON_BY_TYPE = {
  mineral_block: 'marker-mineral',
  camera: 'marker-camera',
  deer_sign: 'marker-deer',
  gate: 'marker-gate',
  feeder: 'marker-feeder'
};

function titleCaseMarker(value) {
  return String(value)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

export function pointRoleOf(feature) {
  return isStandFeature(feature) ? 'stand' : 'marker';
}

export function markerIconIdFor(feature) {
  if (isStandFeature(feature)) return null;
  const savedType = markerTypeSelectValue(feature?.marker_type);
  if (savedType && MARKER_ICON_BY_TYPE[savedType]) {
    return MARKER_ICON_BY_TYPE[savedType];
  }
  if (isFeedFeature(feature)) return 'marker-feeder';
  return 'marker-15';
}

export function markerLabelFor(feature) {
  if (isStandFeature(feature)) return '';
  const savedType = markerTypeSelectValue(feature?.marker_type);
  if (savedType) return titleCaseMarker(savedType);
  if (isFeedFeature(feature)) return 'Feed site';
  return '';
}

/**
 * GeoJSON properties for a map point feature (stands, markers, hunt locations).
 * Wind fields are updated independently via applyPointsWindToMap without rebuilding all layers.
 */
export function buildMapPointProperties(feature, currentWindDegrees) {
  const optimalWinds = getOptimalWindsArray(feature);
  const hasWinds = optimalWinds.length > 0
    || Boolean(feature?.optimal_wind && feature.optimal_wind !== '');
  const windFields = computePointWindFields(feature, currentWindDegrees);
  const stand = isStandFeature(feature);
  const markerType = feature?.marker_type;
  const pointRole = stand ? 'stand' : 'marker';
  const markerIconId = markerIconIdFor(feature);

  return {
    id: feature.id,
    name: feature.name,
    type: feature.type,
    featureType: feature.featureType,
    feature_type: feature.feature_type,
    optimal_wind: feature.optimal_wind,
    optimal_winds: optimalWinds,
    is_stand: stand,
    stand_show: hasWinds ? 'true' : 'false',
    marker_type: markerType,
    marker_label: markerLabelFor(feature),
    point_role: pointRole,
    percent_remaining: feature.percent_remaining,
    estimated_remaining_lbs: feature.estimated_remaining_lbs,
    refill_due_date: feature.refill_due_date,
    sign_out_records: feature.sign_out_records,
    marker_icon_id: markerIconId,
    isMarker: pointRole === 'marker',
    isOccupied: Boolean(feature.isOccupied),
    ...windFields
  };
}

export function buildPointsFeatureCollection(features, currentWindDegrees) {
  const points = (features || []).filter((f) => f?.geojson?.type === 'Point');
  return {
    type: 'FeatureCollection',
    features: points.map((feature) => ({
      type: 'Feature',
      id: feature.id,
      geometry: feature.geojson,
      properties: buildMapPointProperties(feature, currentWindDegrees)
    }))
  };
}

/** Lightweight wind refresh: only updates features-points source (no polygon/line rebuild). */
export function applyPointsWindToMap(map, features, currentWindDegrees) {
  if (!map || typeof map.getSource !== 'function') return false;
  const source = map.getSource('features-points');
  if (!source || typeof source.setData !== 'function') return false;
  const data = buildPointsFeatureCollection(features, currentWindDegrees);
  if (!data.features.length) return false;
  try {
    source.setData(data);
    return true;
  } catch (_) {
    return false;
  }
}
